<script setup lang="ts">
  import { computed, toRef } from 'vue';
  import {
    SearchBar,
    SearchToolbar,
    ColoredButton,
    Pagination,
    SelectInput,
    NoDocuments,
  } from '@/shared/ui';
  import { useAdminListPage } from '../lib';
  import type { EntityType } from '@/shared/lib';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import NoFilesIcon from '@/shared/assets/images/no-files.svg';

  const props = withDefaults(
    defineProps<{
      title: string;
      searchPlaceholder?: string;
      createButtonText?: string;
      entityName: EntityType;
      noFilterResultsMessage?: string;
      emptyStateMessage: string;
      items: unknown[];
      loading: boolean;
      searchTerm: string;
      metadata: { total?: number } | null | undefined;
      selectedQuantity: number | string;
      isUsingFilters: boolean;
      currentPage: number;
      variant?: 'default' | 'stacked' | 'default-pagination';
    }>(),
    {
      searchPlaceholder: 'Buscar',
      createButtonText: '',
      variant: 'default',
    }
  );

  const emit = defineEmits<{
    search: [term: string];
    create: [];
    export: [];
    pageChanged: [page: number];
    'update:selectedQuantity': [value: string | number];
  }>();

  const {
    QUANTITY_OPTIONS,
    totalItems,
    shouldShowPagination,
    shouldShowTotalOnly,
    hasItems,
    shouldShowNoResults,
    shouldShowNoFilterResults,
    shouldShowEmptyState,
    handlePageChange,
    entityPlural,
  } = useAdminListPage({
    items: toRef(props, 'items'),
    loading: toRef(props, 'loading'),
    searchTerm: toRef(props, 'searchTerm'),
    metadata: toRef(props, 'metadata'),
    selectedQuantity: toRef(props, 'selectedQuantity'),
    isUsingFilters: computed(() => props.isUsingFilters),
    onPageChanged: (page: number) => emit('pageChanged', page),
    entityName: props.entityName,
  });

  const shouldShowQuantitySelect = computed(
    () =>
      props.variant === 'stacked' ||
      hasItems.value ||
      props.loading ||
      props.isUsingFilters ||
      Boolean(props.searchTerm)
  );
</script>

<template>
  <div class="m-4 md:m-6">
    <header class="mb-6">
      <h1 class="mt-4 mb-6 font-semibold text-xl text-gray-07">
        {{ title }}
      </h1>
    </header>

    <div class="flex flex-col gap-6">
      <div class="flex gap-3">
        <slot name="action-buttons">
          <ColoredButton
            :text="createButtonText"
            :icon="PlusIcon"
            icon-alt="Ícone de adição"
            @click="emit('create')"
          />
          <ColoredButton
            text="Exportar para Excel"
            :icon="ExportExcelIcon"
            icon-alt="Ícone de exportação"
            :disabled="!hasItems"
            @click="emit('export')"
          />
        </slot>
        <slot
          name="extra-action-buttons"
          :has-items="hasItems"
        />
      </div>

      <SearchToolbar>
        <template #search>
          <slot
            name="search"
            :on-search="(term: string) => emit('search', term)"
          >
            <SearchBar
              :placeholder="searchPlaceholder"
              @search="emit('search', $event)"
            />
          </slot>
        </template>
        <template
          v-if="shouldShowQuantitySelect"
          #quantity
        >
          <SelectInput
            :model-value="selectedQuantity"
            :options="QUANTITY_OPTIONS"
            class="cursor-pointer"
            :class="{ 'border-gray-02 rounded-lg shadow-xs': variant === 'stacked' }"
            @update:model-value="emit('update:selectedQuantity', $event)"
          />
        </template>
      </SearchToolbar>
    </div>

    <div
      v-if="hasItems || isUsingFilters || searchTerm || loading"
      class="mt-6"
    >
      <slot name="table" />
    </div>

    <NoDocuments
      v-else-if="shouldShowEmptyState"
      :title="emptyStateMessage"
      :icon="NoFilesIcon"
    />

    <section
      v-if="shouldShowPagination"
      aria-label="Navegação de páginas"
      class="mt-6"
    >
      <Pagination
        :total-items="totalItems"
        :items-per-page="typeof selectedQuantity === 'number' ? selectedQuantity : 10"
        :current-page="currentPage"
        :entity-name="entityName"
        variant="default"
        @page-changed="handlePageChange"
      />
    </section>
    <section
      v-else-if="shouldShowTotalOnly"
      class="mt-6"
    >
      <p class="text-md pt-2 pl-8 text-gray-03">
        Mostrando total de {{ totalItems }} {{ entityPlural }}
      </p>
    </section>
  </div>
</template>
